/** @jsx jsx */
import { Box } from '@bottlebooks/gatsby-theme-base';
import { Trans } from '@lingui/macro';
import React from 'react';
import { jsx } from 'theme-ui';
import FilterField from '../FilterField';
import FacetBox from './FacetBox';
import FacetValue from './FacetValue';
import useFacetFilter from './useFacetFilter';

const indents = [0, 3, 4];

export default function DefaultFacet({
  title,
  allValues,
  selectedValues,
  onToggle,
  multiSelect,
  pageSize = 5,
  totalCount,
  count,
  placeholder = 'Filter',
}) {
  const [limit, setLimit] = React.useState(pageSize);
  const [filter, setFilter] = React.useState('');
  const allFacetsCount = allValues?.length;
  // Don't show facets that would match all items.
  // - If all products are wines, don't show the "wines" filter.
  // - If all the wines are from Austria, it wouldn't make sense to show a filter on Austria.
  const meaningfulFilters =
    allFacetsCount > 0 && !selectedValues?.length
      ? allValues?.filter(item => {
          if (totalCount && item.count === totalCount) return false;
          if (count && item.count === count) return false;
          return true;
        })
      : allValues;
  const filteredValues = useFacetFilter({
    values: meaningfulFilters,
    filter,
    selectedValues,
  });
  const facetsCount = meaningfulFilters?.length;
  if (!facetsCount) return null;
  const values = filteredValues.slice(0, limit);
  const hasFilter = facetsCount > 3 * pageSize;
  const hasMore = filteredValues.length > values.length;
  const hasLess = limit > pageSize;

  function handleToggle(value, isSelected) {
    onToggle && onToggle(value, isSelected);
    setFilter('');
  }

  return (
    <FacetBox title={title} multiSelect={multiSelect}>
      {hasFilter && (
        <FilterField
          placeholder={placeholder}
          value={filter}
          onChange={filter => setFilter(filter)}
          sx={{ marginBottom: 3 }}
        />
      )}
      {values.map(({ value, label = value, key = value, level, count }) => {
        const isSelected = selectedValues?.includes?.(value);
        return (
          <FacetValue
            key={key}
            count={count}
            isSelected={isSelected}
            onClick={() => handleToggle(value, !isSelected)}
            sx={{ marginLeft: indents[level] }}
          >
            {label}
          </FacetValue>
        );
      })}
      <Box sx={{ marginTop: 3, display: 'flex' }}>
        {hasMore && (
          <FacetValue
            onClick={() => setLimit(limit * 2)}
            sx={{
              color: 'primary',
              backgroundColor: 'transparent',
              variant: 'label.default',
            }}
          >
            <Trans>Show more</Trans>
          </FacetValue>
        )}
        {hasLess && (
          <FacetValue
            onClick={() => setLimit(pageSize)}
            sx={{
              color: 'primary',
              backgroundColor: 'transparent',
              variant: 'label.default',
              marginLeft: 'auto',
            }}
          >
            <Trans>Show less</Trans>
          </FacetValue>
        )}
      </Box>
    </FacetBox>
  );
}
